"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { sendContactEmail, ContactFormData } from "@/actions/emailActions";
import Link from "next/link";
import { cn } from "@/lib/utils";

// Cooldown configuration (5 minutes = 300000 milliseconds)
const COOLDOWN_DURATION = 300000;
const STORAGE_KEY = "contactFormCooldown";

export default function ContactForm({float}: {float?: boolean}) {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});

  // Add cooldown state
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);

  // Load cooldown from localStorage on component mount
  useEffect(() => {
    const storedCooldown = localStorage.getItem(STORAGE_KEY);
    if (storedCooldown) {
      const cooldownTime = parseInt(storedCooldown, 10);
      if (cooldownTime > Date.now()) {
        setCooldownUntil(cooldownTime);
      } else {
        // Clear expired cooldown
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Update countdown timer
  useEffect(() => {
    if (!cooldownUntil) return;

    const updateTimer = () => {
      const now = Date.now();
      if (cooldownUntil > now) {
        setTimeLeft(cooldownUntil - now);
      } else {
        setCooldownUntil(null);
        setTimeLeft(0);
        localStorage.removeItem(STORAGE_KEY);
      }
    };

    // Update immediately and then set interval
    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [cooldownUntil]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check cooldown
    if (cooldownUntil && cooldownUntil > Date.now()) {
      setFormStatus({
        success: false,
        message: `Please wait ${Math.ceil(timeLeft / 1000)} seconds before sending another message.`
      });
      return;
    }

    setIsSubmitting(true);
    setFormStatus({});

    try {
      const result = await sendContactEmail(formData);
      setFormStatus(result);

      if (result.success) {
        // Reset form if successful
        setFormData({ name: "", email: "", message: "" });

        // Set cooldown
        const newCooldownTime = Date.now() + COOLDOWN_DURATION;
        setCooldownUntil(newCooldownTime);
        localStorage.setItem(STORAGE_KEY, newCooldownTime.toString());
      }
    } catch (error) {
      setFormStatus({
        success: false,
        message: "An unexpected error occurred. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format remaining time
  const formatTimeLeft = () => {
    if (!timeLeft) return "";
    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.ceil((timeLeft % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const isDisabled = isSubmitting || (cooldownUntil !== null && cooldownUntil > Date.now());

  return (
    <div className={cn(
      float ? "w-full" : "w-full ick-block-1",
    )}>
      <div className={cn(
        "p-4 sm:p-6 rounded-lg",
        float ? "" : "terminal-glass",
      )}>
        <h3 className="text-xl font-semibold mb-4 sm:mb-6 text-terminal-pink">
          Send a Message
        </h3>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-1">
              Name <span className="text-terminal-pink">*</span>
            </label>
            <input
              id="name"
              type="text"
              placeholder="Your name"
              className="w-full px-3 sm:px-4 py-2 bg-terminal-background/50 border border-terminal-pink/20 rounded-md focus:outline-none focus:ring-2 focus:ring-terminal-pink/30 text-sm"
              required
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting || isDisabled}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              Email <span className="text-terminal-pink">*</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder="your.email@example.com"
              className="w-full px-3 sm:px-4 py-2 bg-terminal-background/50 border border-terminal-pink/20 rounded-md focus:outline-none focus:ring-2 focus:ring-terminal-pink/30 text-sm"
              required
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting || isDisabled}
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium mb-1">
              Message <span className="text-terminal-pink">*</span>
            </label>
            <textarea
              id="message"
              rows={4}
              placeholder="Your message here..."
              className="w-full px-3 sm:px-4 py-2 bg-terminal-background/50 border border-terminal-pink/20 rounded-md focus:outline-none focus:ring-2 focus:ring-terminal-pink/30 text-sm"
              required
              value={formData.message}
              onChange={handleChange}
              disabled={isSubmitting || isDisabled}
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-terminal-pink/80 hover:bg-terminal-pink text-background text-xs sm:text-sm"
            disabled={isDisabled}
          >
            {isSubmitting
              ? "Sending..."
              : cooldownUntil && cooldownUntil > Date.now()
                ? `Please wait ${formatTimeLeft()}s before sending another message`
                : "Send Message"
            }
          </Button>
        </form>
        {formStatus.message && (
          <div
            className={`my-4 p-3 rounded-md text-center ${formStatus.success
              ? "bg-green-500/20 text-green-200"
              : "bg-red-500/20 text-red-200"
              }`}
          >
            {formStatus.message}
          </div>
        )}

        {
          cooldownUntil ? (
              <p className="mt-4 text-xs text-muted-foreground">
                <i>Thank you for reaching out! I will respond to your inquiry from <b className="text-terminal-cyan">contact@iamickdev.com</b>. Please ignore emails from any other address.*</i>
              </p>
          ) : (<p className="mt-2 text-xs text-muted-foreground">
            <i>By sending this message, you agree to our <Link href="/privacy-policy" className="text-terminal-pink">Privacy Policy</Link>.</i>
          </p>)
        }
      </div>
    </div>
  );
}